/** XLSX downloads for one widget or a whole page (one sheet per data widget). */

import type { Catalogue, Widget, WidgetResult } from './reports-api.ts';
import { sheetFileName, widgetSheet } from './widget-sheet.ts';
import { downloadXlsx, type Sheet } from './xlsx.ts';

const sourceOf = (catalogue: Catalogue, widget: Widget) =>
  catalogue.sources.find((source) => source.key === widget.data.source);

/** A download action for one widget, or undefined when it has no numbers to save. */
export function widgetDownload(input: {
  widget: Widget | undefined;
  result: WidgetResult | undefined;
  catalogue: Catalogue;
  fileParts: string[];
}): (() => void) | undefined {
  const { widget } = input;
  if (!widget) return undefined;
  const sheet = widgetSheet(widget, input.result, sourceOf(input.catalogue, widget));
  if (!sheet) return undefined;
  return () => downloadXlsx(sheetFileName([...input.fileParts, sheet.name]), [sheet]);
}

/** Every data widget on the page with numbers, one sheet each; false when there is nothing to save. */
export function downloadPage(input: {
  widgets: Widget[];
  results: Record<string, WidgetResult>;
  keyOf: (index: number) => string;
  catalogue: Catalogue;
  fileParts: string[];
}): boolean {
  const sheets = input.widgets
    .map((widget, index) =>
      widgetSheet(widget, input.results[input.keyOf(index)], sourceOf(input.catalogue, widget)),
    )
    .filter((sheet): sheet is Sheet => sheet !== null);
  if (sheets.length === 0) return false;
  downloadXlsx(sheetFileName(input.fileParts), sheets);
  return true;
}
